// ============================================================================
// AdminFarmersPanel — manage producer accounts (Task #16 Phase A).
// ============================================================================
// Sidebar entry: #admin/farmers. UIF only.
//
// Three surfaces:
//   1. KPI row: total, AMP cohort count, unverified count, unonboarded (no
//      password yet)
//   2. Table: search + filter by program + filter by status. Each row shows
//      email, name, program pills, verified/onboarded state, last login,
//      registration count. Actions: edit, resend invite, deactivate.
//   3. Modals: Add farmer (single row), Bulk import (CSV or paste)
//
// Bulk import parses a simple CSV: email,full_name,phone,programs
// where `programs` is a comma-separated list of codes inside a
// pipe-delimited field (e.g. "amp|lfpp"). See the modal for a template.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2, Plus, RefreshCw, AlertCircle, Mail, Send, Edit2, Archive,
  Users, Upload, CheckCircle2, X as XIcon, Megaphone,
} from 'lucide-react';
import {
  api,
  type AdminFarmer,
  type AdminFarmerCreateInput,
  type AdminFarmerBulkRow,
  type ProgramCode,
} from '../api';

interface Props {
  token: string;
}

const PROGRAMS: Array<{ code: ProgramCode; label: string }> = [
  { code: 'amp',  label: 'AMP cohort' },
  { code: 'lfpp', label: 'LFPP' },
];

export function AdminFarmersPanel({ token }: Props) {
  const [farmers, setFarmers] = useState<AdminFarmer[]>([]);
  const [programCounts, setProgramCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [blastOpen, setBlastOpen] = useState(false);
  const [editing, setEditing] = useState<AdminFarmer | null>(null);
  const [invitingId, setInvitingId] = useState<number | null>(null);
  // Task #17 follow-up: per-row selection for surgical sends. When selected
  // is non-empty, opening the composer targets those specific farmer_ids
  // (bypasses the program filter entirely — most surgical). Cleared when
  // filters change so you can't email someone you can't currently see.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // S6 (047d DROP): LFPP membership now lives in grants.lfpp_participation.
  // This is the count of active participants (the retired program-array metric).
  const [lfppParticipants, setLfppParticipants] = useState<number | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    api.adminFarmers(token, {
      q: search || undefined,
      program: programFilter !== 'all' ? programFilter : undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    })
      .then((d) => {
        setFarmers(d.farmers ?? []);
        const map: Record<string, number> = {};
        for (const c of d.program_counts ?? []) map[c.program] = c.count;
        setProgramCounts(map);
        setLfppParticipants(d.lfpp_participation_count ?? null);
      })
      .catch((err) => setError(err?.message || 'Could not load farmers.'))
      .finally(() => setLoading(false));
  }, [token, search, programFilter, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);
  // Reset selection whenever the filter set changes so we never end up with
  // hidden rows still "selected" behind the scenes.
  useEffect(() => { setSelected(new Set()); }, [search, programFilter, statusFilter]);

  const allVisibleSelected = useMemo(
    () => farmers.length > 0 && farmers.every((f) => selected.has(f.farmer_id)),
    [farmers, selected],
  );

  function toggleOne(farmerId: number) {
    const next = new Set(selected);
    if (next.has(farmerId)) next.delete(farmerId);
    else next.add(farmerId);
    setSelected(next);
  }
  function toggleAllVisible() {
    if (allVisibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(farmers.map((f) => f.farmer_id)));
    }
  }

  async function invite(f: AdminFarmer) {
    setInvitingId(f.farmer_id);
    setError('');
    try {
      await api.inviteFarmer(token, f.farmer_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not send invite.');
    } finally {
      setInvitingId(null);
    }
  }

  async function archive(f: AdminFarmer) {
    if (!confirm(`Deactivate ${f.email}? Historical registrations are preserved; they just can't log in until you re-activate them.`)) return;
    try {
      await api.archiveFarmer(token, f.farmer_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not archive.');
    }
  }

  const totalActive = useMemo(
    () => farmers.filter((f) => f.is_active).length,
    [farmers],
  );
  const unverified = useMemo(
    () => farmers.filter((f) => f.is_active && !f.email_verified).length,
    [farmers],
  );
  const noPassword = useMemo(
    () => farmers.filter((f) => f.is_active && !f.has_password).length,
    [farmers],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Producers</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Farmer accounts. Add producers individually or in bulk; send invites to onboard them. AMP cohort members get program-specific privileges once phases D–F ship.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setBlastOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Megaphone className="h-4 w-4" />
            Send email
          </button>
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Upload className="h-4 w-4" />
            Bulk import
          </button>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" />
            Add farmer
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Active accounts" value={totalActive} tone="ok" icon={<Users className="h-4 w-4" />} />
        <Kpi label="AMP cohort" value={programCounts.amp ?? 0} tone="amp" icon={<Users className="h-4 w-4" />} />
        <Kpi label="Unverified" value={unverified} tone="warn" icon={<AlertCircle className="h-4 w-4" />} />
        <Kpi label="Not onboarded" value={noPassword} tone="warn" icon={<Mail className="h-4 w-4" />} />
      </div>
      {lfppParticipants !== null && (
        <p className="text-xs text-gray-500">
          LFPP participants: <b>{lfppParticipants}</b> — active <code>grants.lfpp_participation</code> rows.
        </p>
      )}

      {/* Filter bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[200px]">
          <span className="block text-xs font-medium text-gray-700 mb-1">Search</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Email or name"
            className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Program</span>
          <select
            value={programFilter}
            onChange={(e) => setProgramFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="all">All</option>
            {PROGRAMS.map((p) => (
              <option key={p.code} value={p.code}>{p.label} ({programCounts[p.code] ?? 0})</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="unverified">Unverified</option>
            <option value="no_password">Not onboarded</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm flex items-center gap-3">
          <span className="font-semibold text-uif-green-dark">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => setBlastOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-1.5 text-xs hover:bg-uif-green-dark"
          >
            <Send className="h-3.5 w-3.5" />
            Email selected ({selected.size})
          </button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="text-xs text-uif-green-dark hover:underline"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible producers"
                />
              </th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Programs</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Regs</th>
              <th className="px-3 py-2">Last login</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-gray-500 text-center"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</td></tr>
            )}
            {!loading && farmers.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-gray-500 italic text-center">No farmers match this filter.</td></tr>
            )}
            {farmers.map((f) => (
              <tr key={f.farmer_id} className={'hover:bg-gray-50 ' + (f.is_active ? '' : 'opacity-50')}>
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(f.farmer_id)}
                    onChange={() => toggleOne(f.farmer_id)}
                    aria-label={`Select ${f.email}`}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-gray-900">
                  {f.email}
                  {/* S3 chore-ledger item 2: portal id visible per producer */}
                  <span className="ml-1.5 text-[10px] text-gray-400 font-mono">#{f.farmer_id}</span>
                </td>
                <td className="px-3 py-2 text-gray-700">{f.full_name}</td>
                <td className="px-3 py-2">
                  {/* S6 (047d DROP): program membership renders from the backing
                      records, not an array — AMP from the current-year enrollment
                      status, LFPP from the lfpp_participation existence flag. */}
                  <div className="flex flex-wrap gap-1">
                    {f.amp_enrollment_status && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-uif-green/10 border-uif-green/30 text-uif-green-dark">
                        AMP · {f.amp_enrollment_status.replace(/_/g, ' ')}
                      </span>
                    )}
                    {f.lfpp_participant && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-uif-green/10 border-uif-green/30 text-uif-green-dark">
                        LFPP
                      </span>
                    )}
                    {!f.amp_enrollment_status && !f.lfpp_participant && (
                      <span className="text-xs text-gray-400 italic">none</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <StatusPills farmer={f} />
                </td>
                <td className="px-3 py-2 text-right text-gray-700">{f.registration_count}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {f.last_login_at
                    ? new Date(f.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    : <span className="italic">never</span>}
                </td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => setEditing(f)}
                    className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                    title="Edit"
                    aria-label={`Edit ${f.email}`}
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  {f.is_active && !f.has_password && (
                    <button
                      type="button"
                      onClick={() => invite(f)}
                      disabled={invitingId === f.farmer_id}
                      className="p-1.5 rounded hover:bg-gray-100 text-uif-green-dark"
                      title={f.invite_sent_at ? 'Resend invite' : 'Send invite'}
                      aria-label={`Send invite to ${f.email}`}
                    >
                      {invitingId === f.farmer_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  )}
                  {f.is_active && (
                    <button
                      type="button"
                      onClick={() => archive(f)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                      title="Deactivate"
                      aria-label={`Deactivate ${f.email}`}
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {addOpen && <AddFarmerModal token={token} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); refresh(); }} />}
      {bulkOpen && <BulkImportModal token={token} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); refresh(); }} />}
      {editing && <EditFarmerModal token={token} farmer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
      {blastOpen && (
        <BlastComposerModal
          token={token}
          programCounts={programCounts}
          initialPrograms={programFilter !== 'all' ? [programFilter as ProgramCode] : []}
          selectedIds={selected.size > 0 ? Array.from(selected) : null}
          selectedFarmers={selected.size > 0 ? farmers.filter((f) => selected.has(f.farmer_id)) : []}
          onClose={() => setBlastOpen(false)}
          onSent={() => { setBlastOpen(false); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone, icon }: { label: string; value: number; tone: 'ok' | 'warn' | 'amp'; icon: React.ReactNode }) {
  const cls =
    tone === 'ok'   ? 'border-emerald-100 bg-emerald-50/50'
    : tone === 'amp' ? 'border-uif-green/20 bg-uif-green/5'
    : 'border-amber-100 bg-amber-50/50';
  return (
    <div className={'rounded-lg border p-3 ' + cls}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-gray-900 mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

function StatusPills({ farmer }: { farmer: AdminFarmer }) {
  if (!farmer.is_active) return <span className="text-xs uppercase tracking-wider text-gray-500">inactive</span>;
  if (!farmer.email_verified && !farmer.invite_sent_at) return <Pill tone="warn">Invite not sent</Pill>;
  if (!farmer.email_verified && farmer.invite_sent_at) return <Pill tone="warn">Invite pending</Pill>;
  if (!farmer.has_password) return <Pill tone="warn">Not onboarded</Pill>;
  return <Pill tone="ok">Active</Pill>;
}

function Pill({ tone, children }: { tone: 'ok' | 'warn'; children: React.ReactNode }) {
  const cls = tone === 'ok'
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-amber-50 border-amber-200 text-amber-800';
  return <span className={'inline-block text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>{children}</span>;
}

// ============================================================================
// Add farmer (single row) modal
// ============================================================================
function AddFarmerModal({ token, onClose, onSaved }: { token: string; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<AdminFarmerCreateInput>({ email: '', full_name: '', phone: '', programs: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function toggleProgram(code: ProgramCode) {
    const next = new Set(form.programs ?? []);
    if (next.has(code)) next.delete(code); else next.add(code);
    setForm({ ...form, programs: Array.from(next) as ProgramCode[] });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      await api.createFarmer(token, {
        ...form,
        phone: form.phone?.trim() || null,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Could not create farmer.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Add farmer" onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Email</span>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Full name</span>
          <input type="text" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Phone (optional)</span>
          <input type="tel" value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <div>
          <span className="block text-xs font-medium text-gray-700 mb-1">Program memberships</span>
          <div className="flex flex-wrap gap-2">
            {PROGRAMS.map((p) => {
              const selected = form.programs?.includes(p.code);
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => toggleProgram(p.code)}
                  disabled={busy}
                  className={
                    'text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ' +
                    (selected
                      ? 'bg-uif-green text-white border-uif-green'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-uif-green')
                  }
                >
                  {selected && '✓ '}{p.label}
                </button>
              );
            })}
          </div>
        </div>
        <p className="text-[11px] text-gray-500">
          After creating, click the paper-plane icon on the row to send the "Set your password" invite email.
        </p>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !form.email || !form.full_name}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add farmer
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Edit farmer modal
// ============================================================================
function EditFarmerModal({ token, farmer, onClose, onSaved }: { token: string; farmer: AdminFarmer; onClose: () => void; onSaved: () => void }) {
  const [fullName, setFullName] = useState(farmer.full_name);
  const [phone, setPhone] = useState(farmer.phone ?? '');
  // S6 (047d DROP): current membership derived from the backing records, not an array.
  const [programs, setPrograms] = useState<Set<ProgramCode>>(new Set<ProgramCode>([
    ...(farmer.amp_enrollment_status ? ['amp' as ProgramCode] : []),
    ...(farmer.lfpp_participant ? ['lfpp' as ProgramCode] : []),
  ]));
  const [isActive, setIsActive] = useState(farmer.is_active);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function toggleProgram(code: ProgramCode) {
    const next = new Set(programs);
    if (next.has(code)) next.delete(code); else next.add(code);
    setPrograms(next);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      await api.updateFarmer(token, farmer.farmer_id, {
        full_name: fullName,
        phone: phone.trim() || null,
        programs: Array.from(programs),
        is_active: isActive,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Could not update.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title={`Edit ${farmer.email}`} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Full name</span>
          <input type="text" required value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-700 mb-1">Phone</span>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none" />
        </label>
        <div>
          <span className="block text-xs font-medium text-gray-700 mb-1">Program memberships</span>
          <div className="flex flex-wrap gap-2">
            {PROGRAMS.map((p) => {
              const selected = programs.has(p.code);
              return (
                <button
                  key={p.code}
                  type="button"
                  onClick={() => toggleProgram(p.code)}
                  disabled={busy}
                  className={
                    'text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ' +
                    (selected
                      ? 'bg-uif-green text-white border-uif-green'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-uif-green')
                  }
                >
                  {selected && '✓ '}{p.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            Adding a program enrolls the producer. Un-checking one here does <strong>not</strong> withdraw them — withdrawal is a separate action with its own audit trail.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={busy} />
          Active
        </label>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Save
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Bulk import modal — paste CSV, optionally send invites
// ============================================================================
function BulkImportModal({ token, onClose, onDone }: { token: string; onClose: () => void; onDone: () => void }) {
  const [csv, setCsv] = useState('');
  const [defaultProgram, setDefaultProgram] = useState<ProgramCode | ''>('amp');
  const [sendInvites, setSendInvites] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number; invites_sent: number; invites_failed: number } | null>(null);
  const [error, setError] = useState('');

  function parseCsv(): AdminFarmerBulkRow[] {
    const rows: AdminFarmerBulkRow[] = [];
    for (const raw of csv.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line || line.startsWith('#')) continue;
      // Split by comma, respecting simple quoted fields. Keep it simple —
      // this is a UIF admin tool, not user-uploaded data.
      const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
      const [email, full_name, phone, programs] = parts;
      if (!email || !full_name) continue;
      // Skip a likely header row.
      if (email.toLowerCase() === 'email' && full_name.toLowerCase().includes('name')) continue;
      const programCodes: ProgramCode[] = programs
        ? (programs.split('|').map((s) => s.trim().toLowerCase()).filter((c): c is ProgramCode => c === 'amp' || c === 'lfpp'))
        : (defaultProgram ? [defaultProgram] : []);
      rows.push({
        email,
        full_name,
        phone: phone || null,
        programs: programCodes,
      });
    }
    return rows;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError(''); setResult(null);
    const rows = parseCsv();
    if (rows.length === 0) {
      setError('No valid rows found.');
      setBusy(false);
      return;
    }
    if (!confirm(`Import ${rows.length} farmer${rows.length === 1 ? '' : 's'}?${sendInvites ? ' Invite emails will be sent.' : ''}`)) {
      setBusy(false);
      return;
    }
    try {
      const r = await api.bulkFarmers(token, rows, sendInvites);
      setResult(r);
    } catch (err: any) {
      setError(err?.message || 'Bulk import failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Bulk import farmers" onClose={onClose}>
      {result ? (
        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900">
            <div className="font-semibold">Imported {result.created} farmer{result.created === 1 ? '' : 's'}</div>
            <div className="text-xs mt-1">
              Skipped: {result.skipped} · Invites sent: {result.invites_sent} · Invite failures: {result.invites_failed}
            </div>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={onDone}
              className="rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark">
              Done
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-gray-600">
            One row per farmer: <code className="bg-gray-100 px-1 rounded">email,full_name,phone,programs</code>. Programs are pipe-delimited codes like <code className="bg-gray-100 px-1 rounded">amp</code> or <code className="bg-gray-100 px-1 rounded">amp|lfpp</code>. Blank programs use the default below.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <label className="text-xs text-gray-700 flex items-center gap-2">
              Default program:
              <select value={defaultProgram} onChange={(e) => setDefaultProgram(e.target.value as any)} disabled={busy}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs">
                <option value="">— none —</option>
                {PROGRAMS.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-gray-700 inline-flex items-center gap-2">
              <input type="checkbox" checked={sendInvites} onChange={(e) => setSendInvites(e.target.checked)} disabled={busy} />
              Send invite emails after import
            </label>
          </div>
          <textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            disabled={busy}
            rows={12}
            placeholder={`# email,full_name,phone,programs\njane@example.com,Jane Farmer,555-1234,amp\njohn@example.com,John Grower,,amp|lfpp`}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-xs font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={busy}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={busy || !csv.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

// ============================================================================
// Blast composer — program-scoped email to farmer accounts (Task #17)
// ============================================================================
function BlastComposerModal({
  token, programCounts, initialPrograms, selectedIds, selectedFarmers, onClose, onSent,
}: {
  token: string;
  programCounts: Record<string, number>;
  initialPrograms: ProgramCode[];
  selectedIds: number[] | null;
  selectedFarmers: AdminFarmer[];
  onClose: () => void;
  onSent: () => void;
}) {
  const inSelectionMode = selectedIds != null && selectedIds.length > 0;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(
    'Hi {first_name},\n\n' +
    '\n\n' +
    '— Up in Farms team',
  );
  const [programs, setPrograms] = useState<Set<ProgramCode>>(new Set(initialPrograms));
  const [onlyOnboarded, setOnlyOnboarded] = useState(true);
  const [includeUnsub, setIncludeUnsub] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ sent: number; failed: number; total: number } | null>(null);

  function toggleProgram(code: ProgramCode) {
    const next = new Set(programs);
    if (next.has(code)) next.delete(code); else next.add(code);
    setPrograms(next);
  }

  // Estimated recipient count based on the KPI counts. Doesn't perfectly
  // account for onlyOnboarded filter, but gives a ballpark before send.
  const estimateCount = useMemo(() => {
    if (programs.size === 0) return null;   // "all active" — unknown from panel state
    let total = 0;
    for (const p of programs) total += programCounts[p] ?? 0;
    return total;
  }, [programs, programCounts]);

  const recipientLabel = inSelectionMode
    ? `${selectedIds!.length} selected producer${selectedIds!.length === 1 ? '' : 's'}`
    : programs.size === 0
      ? 'all active producers'
      : `producers in ${Array.from(programs).map((p) => PROGRAMS.find((x) => x.code === p)?.label ?? p).join(' or ')}`;

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (sending) return;
    const promptCount = inSelectionMode
      ? ''
      : (estimateCount != null ? `${estimateCount} ` : '');
    if (!confirm(`Send "${subject}" to ${promptCount}${recipientLabel}? This cannot be un-sent.`)) return;
    setSending(true); setError('');
    try {
      const r = await api.farmerBlast(token, {
        subject: subject.trim(),
        body_md: body.trim(),
        // In selection mode, farmer_ids overrides programs + only_onboarded
        // on the backend — surgical send to specific people regardless of
        // membership. Otherwise use the program filter + onboarded gate.
        ...(inSelectionMode
          ? { farmer_ids: selectedIds! }
          : { programs: Array.from(programs), only_onboarded: onlyOnboarded }),
        include_unsub: includeUnsub,
      });
      setResult({ sent: r.sent, failed: r.failed, total: r.total });
      setTimeout(onSent, 2000);
    } catch (err: any) {
      setError(err?.message || 'Send failed.');
    } finally {
      setSending(false);
    }
  }

  return (
    <ModalShell title="Send email to producers" onClose={onClose}>
      {result ? (
        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-900">
            <div className="font-semibold">Sent {result.sent} of {result.total}</div>
            {result.failed > 0 && <div className="text-xs mt-1">{result.failed} failed to send.</div>}
          </div>
        </div>
      ) : (
        <form onSubmit={send} className="space-y-3">
          {inSelectionMode ? (
            <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm">
              <div className="font-semibold text-uif-green-dark mb-1">
                Sending to {recipientLabel}
              </div>
              <div className="text-xs text-gray-700">
                {selectedFarmers.slice(0, 5).map((f) => f.email).join(', ')}
                {selectedFarmers.length > 5 && ` and ${selectedFarmers.length - 5} more`}
              </div>
            </div>
          ) : (
            <>
              <div>
                <span className="block text-xs font-medium text-gray-700 mb-1">Recipient program(s)</span>
                <div className="flex flex-wrap gap-2">
                  {PROGRAMS.map((p) => {
                    const selected = programs.has(p.code);
                    return (
                      <button
                        key={p.code}
                        type="button"
                        onClick={() => toggleProgram(p.code)}
                        disabled={sending}
                        className={
                          'text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ' +
                          (selected
                            ? 'bg-uif-green text-white border-uif-green'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-uif-green')
                        }
                      >
                        {selected && '✓ '}{p.label} ({programCounts[p.code] ?? 0})
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Leave empty to send to <strong>all active producers</strong>. Otherwise, only members of the selected program(s) receive this email.
                </p>
              </div>

              <div className="bg-uif-green/5 border border-uif-green/30 rounded-md px-3 py-2 text-sm">
                Sending to <strong>{estimateCount != null ? `~${estimateCount} ` : ''}{recipientLabel}</strong>
                {onlyOnboarded && <span className="text-xs text-gray-600"> (skipping producers who haven't set a password)</span>}.
              </div>
            </>
          )}

          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Subject</span>
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={255}
              placeholder="e.g. Reminder: your first milestone deadline is next week"
              disabled={sending}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
          </label>

          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">Body (markdown)</span>
            <textarea
              required
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              disabled={sending}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Placeholders: <code>{'{first_name}'}</code>, <code>{'{full_name}'}</code>, <code>{'{email}'}</code>. Markdown supported (headings, lists, bold, italic, links).
            </p>
          </label>

          <div className="flex flex-wrap gap-4">
            {!inSelectionMode && (
              <label className="text-xs text-gray-700 inline-flex items-center gap-1.5">
                <input type="checkbox" checked={onlyOnboarded} onChange={(e) => setOnlyOnboarded(e.target.checked)} disabled={sending} />
                Only send to onboarded producers
              </label>
            )}
            <label className="text-xs text-gray-700 inline-flex items-center gap-1.5">
              <input type="checkbox" checked={includeUnsub} onChange={(e) => setIncludeUnsub(e.target.checked)} disabled={sending} />
              Include unsubscribe footer
            </label>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={sending}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={sending || !subject.trim() || !body.trim()}
              className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send
            </button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-uif-green-dark">{title}</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
